// FIXME: Finetune with our code.
import { combineLatest as observableCombineLatest, concat as observableConcat, fromEvent as observableFromEvent, merge as observableMerge, of as observableOf, BehaviorSubject, Subject, } from "rxjs";
import { bufferCount, distinctUntilChanged, filter, first, map, publishReplay, refCount, scan, share, startWith, switchMap, take, takeUntil, withLatestFrom, } from "rxjs/operators";
import { SubscriptionHolder } from "../utils/SubscriptionHolder";
// MouseEvent.button
const LEFT_BUTTON = 0;
const RIGHT_BUTTON = 2;
// MouseEvent.buttons
const BUTTONS_MAP = {
    [LEFT_BUTTON]: 1,
    [RIGHT_BUTTON]: 2,
};
export class MouseService {
    constructor(container, canvasContainer, domContainer, doc) {
        this._subscriptions = new SubscriptionHolder();
        const subs = this._subscriptions;
        this._activeSubject$ = new BehaviorSubject(false);
        this._active$ = this._activeSubject$.pipe(distinctUntilChanged(), publishReplay(1), refCount());
        this._claimMouse$ = new Subject();
        this._claimWheel$ = new Subject();
        this._deferPixelClaims$ = new Subject();
        this._deferPixels$ = this._deferPixelClaims$.pipe(scan((claims, claim) => {
            if (claim.deferPixels == null) {
                delete claims[claim.name];
            }
            else {
                claims[claim.name] = claim.deferPixels;
            }
            return claims;
        }, {}), map((claims) => {
            let deferPixelMax = -1;
            for (const key in claims) {
                if (!Object.prototype.hasOwnProperty.call(claims, key)) {
                    continue;
                }
                const deferPixels = claims[key];
                if (deferPixels > deferPixelMax) {
                    deferPixelMax = deferPixels;
                }
            }
            return deferPixelMax;
        }), startWith(-1), publishReplay(1), refCount());
        subs.push(this._deferPixels$.subscribe(() => {
            /* noop */
        }));
        this._documentMouseMove$ = observableFromEvent(doc, "mousemove");
        this._documentMouseUp$ = observableFromEvent(doc, "mouseup");
        this._mouseDown$ = observableFromEvent(canvasContainer, "mousedown");
        this._mouseEnter$ = observableFromEvent(canvasContainer, "mouseenter");
        this._mouseLeave$ = observableFromEvent(canvasContainer, "mouseleave");
        this._mouseMove$ = observableFromEvent(canvasContainer, "mousemove");
        this._mouseUp$ = observableFromEvent(canvasContainer, "mouseup");
        this._mouseOut$ = observableFromEvent(canvasContainer, "mouseout");
        this._mouseOver$ = observableFromEvent(canvasContainer, "mouseover");
        this._domMouseDown$ = observableFromEvent(domContainer, "mousedown");
        this._domMouseMove$ = observableFromEvent(domContainer, "mousemove");
        this._click$ = observableFromEvent(canvasContainer, "click");
        this._contextMenu$ = observableFromEvent(canvasContainer, "contextmenu");
        this._windowBlur$ = observableFromEvent(window, "blur");
        this._dblClick$ = observableMerge(observableFromEvent(container, "click"), observableFromEvent(canvasContainer, "dblclick")).pipe(bufferCount(3, 1), filter((events) => {
            const event1 = events[0];
            const event2 = events[1];
            const event3 = events[2];
            return (event1.type === "click" &&
                event2.type === "click" &&
                event3.type === "dblclick" &&
                event1.target.parentNode === canvasContainer &&
                event2.target.parentNode === canvasContainer);
        }), map((events) => {
            return events[2];
        }), share());
        subs.push(observableMerge(this._domMouseDown$, this._domMouseMove$, this._dblClick$, this._contextMenu$).subscribe((event) => {
            event.preventDefault();
        }));
        this._mouseWheel$ = observableMerge(observableFromEvent(canvasContainer, "wheel"), observableFromEvent(domContainer, "wheel")).pipe(share());
        this._consistentContextMenu$ = observableMerge(this._mouseDown$, this._mouseMove$, this._mouseOut$, this._mouseUp$, this._contextMenu$).pipe(bufferCount(3, 1), filter((events) => {
            // fire context menu on mouse up both on mac and windows
            return (events[0].type === "mousedown" && events[1].type === "contextmenu" && events[2].type === "mouseup");
        }), map((events) => {
            return events[1];
        }), share());
        const dragStop$ = observableMerge(this._windowBlur$, this._documentMouseMove$.pipe(filter((e) => {
            return this._buttonReleased(e, LEFT_BUTTON);
        })), this._documentMouseUp$.pipe(filter((e) => {
            return this._mouseButton(e) === LEFT_BUTTON;
        }))).pipe(share());
        const mouseDragInitiate$ = this._createMouseDragInitiate$(LEFT_BUTTON, this._mouseDown$, dragStop$, true).pipe(share());
        this._mouseDragStart$ = this._createMouseDragStart$(mouseDragInitiate$).pipe(share());
        this._mouseDrag$ = this._createMouseDrag$(mouseDragInitiate$, dragStop$).pipe(share());
        this._mouseDragEnd$ = this._createMouseDragEnd$(this._mouseDragStart$, dragStop$).pipe(share());
        const domMouseDragInitiate$ = this._createMouseDragInitiate$(LEFT_BUTTON, this._domMouseDown$, dragStop$, false).pipe(share());
        this._domMouseDragStart$ = this._createMouseDragStart$(domMouseDragInitiate$).pipe(share());
        this._domMouseDrag$ = this._createMouseDrag$(domMouseDragInitiate$, dragStop$).pipe(share());
        this._domMouseDragEnd$ = this._createMouseDragEnd$(this._domMouseDragStart$, dragStop$).pipe(share());
        const rightDragStop$ = observableMerge(this._windowBlur$, this._documentMouseMove$.pipe(filter((e) => {
            return this._buttonReleased(e, RIGHT_BUTTON);
        })), this._documentMouseUp$.pipe(filter((e) => {
            return this._mouseButton(e) === RIGHT_BUTTON;
        }))).pipe(share());
        const mouseRightDragInitiate$ = this._createMouseDragInitiate$(RIGHT_BUTTON, this._mouseDown$, rightDragStop$, true).pipe(share());
        this._mouseRightDragStart$ = this._createMouseDragStart$(mouseRightDragInitiate$).pipe(share());
        this._mouseRightDrag$ = this._createMouseDrag$(mouseRightDragInitiate$, rightDragStop$).pipe(share());
        this._mouseRightDragEnd$ = this._createMouseDragEnd$(this._mouseRightDragStart$, rightDragStop$).pipe(share());
        this._proximateClick$ = this._mouseDown$.pipe(switchMap((mouseDown) => {
            return this._click$.pipe(takeUntil(this._createDeferredMouseMove$(mouseDown, this._documentMouseMove$)), take(1));
        }), share());
        this._staticClick$ = this._mouseDown$.pipe(switchMap(() => {
            return this._click$.pipe(takeUntil(this._documentMouseMove$), take(1));
        }), share());
        subs.push(this._mouseDragStart$.subscribe());
        subs.push(this._mouseDrag$.subscribe());
        subs.push(this._mouseDragEnd$.subscribe());
        subs.push(this._domMouseDragStart$.subscribe());
        subs.push(this._domMouseDrag$.subscribe());
        subs.push(this._domMouseDragEnd$.subscribe());
        subs.push(this._mouseRightDragStart$.subscribe());
        subs.push(this._mouseRightDrag$.subscribe());
        subs.push(this._mouseRightDragEnd$.subscribe());
        subs.push(this._staticClick$.subscribe());
        this._mouseOwner$ = this._createOwner$(this._claimMouse$).pipe(publishReplay(1), refCount());
        this._wheelOwner$ = this._createOwner$(this._claimWheel$).pipe(publishReplay(1), refCount());
        subs.push(this._mouseOwner$.subscribe(() => {
            /* noop */
        }));
        subs.push(this._wheelOwner$.subscribe(() => {
            /* noop */
        }));
    }
    get active$() {
        return this._active$;
    }
    get activate$() {
        return this._activeSubject$;
    }
    get documentMouseMove$() {
        return this._documentMouseMove$;
    }
    get documentMouseUp$() {
        return this._documentMouseUp$;
    }
    get domMouseDragStart$() {
        return this._domMouseDragStart$;
    }
    get domMouseDrag$() {
        return this._domMouseDrag$;
    }
    get domMouseDragEnd$() {
        return this._domMouseDragEnd$;
    }
    get domMouseDown$() {
        return this._domMouseDown$;
    }
    get domMouseMove$() {
        return this._domMouseMove$;
    }
    get mouseOwner$() {
        return this._mouseOwner$;
    }
    get mouseDown$() {
        return this._mouseDown$;
    }
    get mouseEnter$() {
        return this._mouseEnter$;
    }
    get mouseMove$() {
        return this._mouseMove$;
    }
    get mouseLeave$() {
        return this._mouseLeave$;
    }
    get mouseOut$() {
        return this._mouseOut$;
    }
    get mouseOver$() {
        return this._mouseOver$;
    }
    get mouseUp$() {
        return this._mouseUp$;
    }
    get click$() {
        return this._click$;
    }
    get dblClick$() {
        return this._dblClick$;
    }
    get contextMenu$() {
        return this._consistentContextMenu$;
    }
    get mouseWheel$() {
        return this._mouseWheel$;
    }
    get mouseDragStart$() {
        return this._mouseDragStart$;
    }
    get mouseDrag$() {
        return this._mouseDrag$;
    }
    get mouseDragEnd$() {
        return this._mouseDragEnd$;
    }
    get mouseRightDragStart$() {
        return this._mouseRightDragStart$;
    }
    get mouseRightDrag$() {
        return this._mouseRightDrag$;
    }
    get mouseRightDragEnd$() {
        return this._mouseRightDragEnd$;
    }
    get proximateClick$() {
        return this._proximateClick$;
    }
    get staticClick$() {
        return this._staticClick$;
    }
    get windowBlur$() {
        return this._windowBlur$;
    }
    dispose() {
        this._subscriptions.unsubscribe();
    }
    claimMouse(name, zindex) {
        this._claimMouse$.next({ name: name, zindex: zindex });
    }
    unclaimMouse(name) {
        this._claimMouse$.next({ name: name, zindex: null });
    }
    deferPixels(name, deferPixels) {
        this._deferPixelClaims$.next({ name: name, deferPixels: deferPixels });
    }
    undeferPixels(name) {
        this._deferPixelClaims$.next({ name: name, deferPixels: null });
    }
    claimWheel(name, zindex) {
        this._claimWheel$.next({ name: name, zindex: zindex });
    }
    unclaimWheel(name) {
        this._claimWheel$.next({ name: name, zindex: null });
    }
    filtered$(name, observable$) {
        return this._filtered(name, observable$, this._mouseOwner$);
    }
    filteredWheel$(name, observable$) {
        return this._filtered(name, observable$, this._wheelOwner$);
    }
    _createDeferredMouseMove$(origin, mouseMove$) {
        return mouseMove$.pipe(map((mouseMove) => {
            const deltaX = mouseMove.clientX - origin.clientX;
            const deltaY = mouseMove.clientY - origin.clientY;
            return [mouseMove, Math.sqrt(deltaX * deltaX + deltaY * deltaY)];
        }), withLatestFrom(this._deferPixels$), filter(([[, delta], deferPixels]) => {
            return delta > deferPixels;
        }), map(([[mouseMove]]) => {
            return mouseMove;
        }));
    }
    _createMouseDrag$(mouseDragStartInitiate$, stop$) {
        return mouseDragStartInitiate$.pipe(map(([, mouseMove]) => {
            return mouseMove;
        }), switchMap((mouseMove) => {
            return observableConcat(observableOf(mouseMove), this._documentMouseMove$).pipe(takeUntil(stop$));
        }));
    }
    _createMouseDragEnd$(mouseDragStart$, stop$) {
        return mouseDragStart$.pipe(switchMap(() => {
            return stop$.pipe(first());
        }));
    }
    _createMouseDragStart$(mouseDragStartInitiate$) {
        return mouseDragStartInitiate$.pipe(map(([mouseDown]) => {
            return mouseDown;
        }));
    }
    _createMouseDragInitiate$(button, mouseDown$, stop$, defer) {
        return mouseDown$.pipe(filter((mouseDown) => {
            return this._mouseButton(mouseDown) === button;
        }), switchMap((mouseDown) => {
            return observableCombineLatest(observableOf(mouseDown), defer
                ? this._createDeferredMouseMove$(mouseDown, this._documentMouseMove$)
                : this._documentMouseMove$).pipe(takeUntil(stop$), take(1));
        }));
    }
    _createOwner$(claim$) {
        return claim$.pipe(scan((claims, claim) => {
            if (claim.zindex == null) {
                delete claims[claim.name];
            }
            else {
                claims[claim.name] = claim.zindex;
            }
            return claims;
        }, {}), map((claims) => {
            let owner = null;
            let zIndexMax = -1;
            for (const name in claims) {
                if (!Object.prototype.hasOwnProperty.call(claims, name)) {
                    continue;
                }
                if (claims[name] > zIndexMax) {
                    zIndexMax = claims[name];
                    owner = name;
                }
            }
            return owner;
        }), startWith(null));
    }
    _filtered(name, observable$, owner$) {
        return observable$.pipe(withLatestFrom(owner$), filter(([, owner]) => {
            return owner === name;
        }), map(([item]) => {
            return item;
        }));
    }
    _mouseButton(event) {
        const upOrDown = event.type === "mousedown" || event.type === "mouseup";
        const InstallTrigger = window.InstallTrigger;
        if (upOrDown &&
            typeof InstallTrigger !== "undefined" &&
            event.button === RIGHT_BUTTON &&
            event.ctrlKey &&
            window.navigator.platform.toUpperCase().indexOf("MAC") >= 0) {
            // Fix for the fact that Firefox (detected by InstallTrigger)
            // on Mac determines e.button = 2 when using Control + left click.
            return LEFT_BUTTON;
        }
        return event.button;
    }
    _buttonReleased(e, button) {
        // Right button `mouseup` is not fired in
        // Chrome on Mac outside the window or iframe. If
        // the button is no longer pressed during move
        // it may have been released and drag stop
        // should be emitted.
        const flag = BUTTONS_MAP[button];
        return e.buttons === undefined || (e.buttons & flag) !== flag;
    }
}
//# sourceMappingURL=MouseService.js.map